import type { PropsWithChildren } from 'react'
import * as React from 'react'
import clsx from 'clsx'
import styled from 'styled-components'
import { rem } from 'polished'
import InputText from 'LuxKit/Typography/InputText'
import zIndex from 'styles/tools/z-index'

const Container = styled.span`
  display: block;
`

const Wrapper = styled.span`
  position: relative;
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.S};
  box-shadow: none;
  transition: border-color .2s, color 0.2s, background-color 0.2s;
  display: block;
  color: ${props => props.theme.palette.neutral.surface.normalContrast};

  &:not(.error):not(.disabled) {
    &:focus-within {
      border-color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
    }
  }

  &.focus-within {
    border-color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
    z-index: ${zIndex.min};
  }

  &:not(.disabled) {
    &.error {
      border-color: ${props => props.theme.palette.messaging.critical.normalForeground};
    }
  }

  &.disabled {
    color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
    background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
    cursor: not-allowed;
  }
`

const ValidationMessage = styled(InputText)`
  box-sizing: border-box;
  min-height: ${rem(20)};
  padding-top: ${rem(4)};
  pointer-events: none;
  transition: opacity 0.2s;
  opacity: 0;

  &.visible {
    opacity: 1;
    pointer-events: auto;
  }

  &.right {
    text-align: end;
  }
`

const IconWrapper = styled.div`
  display: flex;
  transform: translateY(-50%);
  position: absolute;
  top: 50%;
  pointer-events: none;

  > * {
    pointer-events: auto;
  }

  &.left {
    inset-inline-start: ${rem(12)};
  }

  &.right {
    inset-inline-end: ${rem(12)};
  }
`

interface Props extends React.HTMLAttributes<HTMLElement> {
  className?: string
  error?: string
  disabled?: boolean
  noValidationMessage?: boolean
  noValidationSpacing?: boolean
  startIcon?: React.ReactNode
  endIcon?: React.ReactNode
  helpText?: React.ReactNode
  helpTextPos?: 'left' | 'right'
}

const InputWrap = React.forwardRef<HTMLSpanElement | null, PropsWithChildren<Props>>((props, ref) => {
  const {
    className,
    children,
    error,
    disabled,
    noValidationMessage,
    noValidationSpacing,
    startIcon,
    helpText,
    helpTextPos = 'left',
    endIcon,
    ...otherProps
  } = props

  const helpTextTitle = typeof helpText === 'string' ? helpText : undefined
  const showValidationMessage = !noValidationMessage && (!disabled || helpText)
  const validationMessage = showValidationMessage ? error || helpText : undefined
  const renderValidationSlot = !noValidationSpacing || !!validationMessage
  const inputWrapper = (
    <Wrapper
      {...otherProps}
      ref={ref}
      className={clsx(
        className,
        {
          error: !!error,
          disabled,
        })}
    >
      {startIcon && <IconWrapper className="left">{startIcon}</IconWrapper>}
      {children}
      {endIcon && <IconWrapper className="right">{endIcon}</IconWrapper>}
    </Wrapper>
  )

  if (noValidationSpacing && noValidationMessage) {
    return inputWrapper
  }

  return (
    <Container>
      {inputWrapper}
      {renderValidationSlot && <ValidationMessage
        className={clsx({
          visible: !!validationMessage,
          right: helpTextPos === 'right',
        })}
        title={error || helpTextTitle}
        variant="caption"
        colour={error ? 'critical' : 'light-foreground-alt'}
        lineClamp={2}
      >
        {validationMessage}
      </ValidationMessage>}
    </Container>
  )
})

InputWrap.displayName = 'InputWrap'

export default InputWrap
